import { CloseCircle } from "@/components/newIcons/CloseCircle";
import { dataStore, filteredValuesStore, setTypeValues } from "@/components/search/_utils/searchStore";
import type { Value } from "@/components/search/_utils/types";
import { useStore } from "@nanostores/react";
import { useEffect, useRef, useState } from "react";
import { Collapse } from "../Collapse";
import { drawerTypeStore } from "../store";
import { SpeakerBlock } from "./SpeakerBlock";

type SpeakerProps = {
  speaker: Value;
  onClick: (value: Value) => void;
};
const Speaker = ({ speaker, onClick }: SpeakerProps) => {
  return (
    <span className="text-pr-1 flex items-center cursor-pointer text-sm font-bold" onClick={() => onClick(speaker)}>
      {speaker.value}
      <CloseCircle className="text-primary w-4 h-4 ml-1" />
    </span>
  );
};

type SearchProps = {
  onAdd: (value: Value) => void;
  onRemove: (value: Value) => void;
  localSpeakers: Array<Value>;
  dataSpeakers: Array<{
    key: string;
    count: number;
  }>;
};
const SearchSpeakers = ({ onAdd, onRemove, localSpeakers, dataSpeakers }: SearchProps) => {
  const [search, setSearch] = useState("");
  const [filtered, setFiltered] = useState(dataSpeakers);

  return (
    <>
      <input
        placeholder="Rechercher un conférencier"
        type="text"
        className="w-full text-base font-semibold border border-[#E5E5E5] rounded-sm py-3 px-5 focus:outline-hidden mt-5"
        value={search}
        onChange={async (e) => {
          const v = e.target.value;
          setSearch(v);

          const Fuse = (await import("fuse.js")).default;
          const fuse = new Fuse(dataSpeakers, {
            keys: ["fullname"],
            threshold: 0.4,
            distance: 75,
          });

          const result = fuse.search(v);

          if (result.length === 0) {
            setFiltered(dataSpeakers);
          } else {
            setFiltered(result.map((r) => r.item));
          }
        }}
      />
      {localSpeakers.length !== 0 && (
        <div className="flex flex-col mt-5 space-y-1">
          {localSpeakers.map((speaker) => (
            <Speaker key={speaker.value} speaker={speaker} onClick={onRemove} />
          ))}
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-5 mt-5">
        {dataSpeakers
          .filter((s) => !localSpeakers.some((l) => l.value === s.key))
          .filter((s) => filtered.some((l) => l.key === s.key))
          .slice(0, 10)
          .map((speaker) => (
            <SpeakerBlock
              key={speaker.key}
              speaker={speaker.key}
              onClick={(speaker) =>
                onAdd({
                  type: "speakers",
                  value: speaker,
                })
              }
            />
          ))}
      </div>
    </>
  );
};

type NotSearchProps = {
  localSpeakers: Array<Value>;
  handleRemove: (value: Value) => void;
  handleAdd: (value: Value) => void;
  dataSpeakers: Array<{
    key: string;
    count: number;
  }>;
};
const NotSearch = ({ localSpeakers, handleRemove, handleAdd, dataSpeakers }: NotSearchProps) => {
  return (
    <>
      {localSpeakers.length !== 0 && (
        <div className="flex flex-col mt-5 space-y-1">
          {localSpeakers.map((speaker) => (
            <Speaker key={speaker.value} speaker={speaker} onClick={handleRemove} />
          ))}
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-5 mt-5">
        {dataSpeakers
          .filter((s) => !localSpeakers.some((l) => l.value === s.key))
          .slice(0, 10)
          .map((speaker) => (
            <SpeakerBlock
              key={speaker.key}
              speaker={speaker.key}
              onClick={(speaker) =>
                handleAdd({
                  type: "speakers",
                  value: speaker,
                })
              }
            />
          ))}
      </div>
    </>
  );
};

export const Speakers = () => {
  const state = useStore(drawerTypeStore);
  const selectedSpeakers = useStore(filteredValuesStore("speakers"));
  const [localSpeakers, setLocalSpeakers] = useState(selectedSpeakers);
  const selectedSpeakersRef = useRef(selectedSpeakers);
  const data = useStore(dataStore);

  useEffect(() => {
    const isDiff = () => {
      const oldS = selectedSpeakersRef.current.sort((a, b) => a.value.localeCompare(b.value));

      const newS = selectedSpeakers.sort((a, b) => a.value.localeCompare(b.value));

      if (oldS.length !== newS.length) return true;

      for (let index = 0; index < newS.length; index++) {
        if (oldS[index] !== newS[index]) return true;
      }

      return false;
    };

    if (isDiff()) {
      selectedSpeakersRef.current = selectedSpeakers;
      setLocalSpeakers(selectedSpeakers);
    }
  }, [selectedSpeakers]);

  const handleAdd = (value: Value) => {
    setLocalSpeakers((s) => [...s, value]);
  };

  const handleRemove = (value: Value) => {
    setLocalSpeakers((s) => s.filter((si) => si.value !== value.value));
  };

  const handleOpen = () => {
    drawerTypeStore.set(state === "speakers" ? null : "speakers");
  };

  const handleClickOutside = () => {
    setTypeValues("speakers", localSpeakers);

    drawerTypeStore.set(null);
  };

  const lessThan8 = (data?.speakers.length || 0) < 8;

  return (
    <Collapse
      open={state === "speakers"}
      title="Conférenciers"
      onclick={handleOpen}
      onClickOutside={handleClickOutside}
      count={selectedSpeakers.length}
    >
      {!lessThan8 && (
        <SearchSpeakers
          onAdd={handleAdd}
          onRemove={handleRemove}
          localSpeakers={localSpeakers}
          dataSpeakers={data?.speakers || []}
        />
      )}
      {lessThan8 && (
        <NotSearch
          handleAdd={handleAdd}
          handleRemove={handleRemove}
          localSpeakers={localSpeakers}
          dataSpeakers={data?.speakers || []}
        />
      )}
    </Collapse>
  );
};
